import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SmoothScroll } from "@/components/SmoothScroll";
import Home from "@/pages/Home";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LegalPage from "@/pages/LegalPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import NotFoundPage from "@/pages/NotFoundPage";
import StudioHome from "@/pages/studio/StudioHome";
import StudioDossiers from "@/pages/studio/StudioDossiers";
import StudioDossier from "@/pages/studio/StudioDossier";
import StudioQuote from "@/pages/studio/StudioQuote";
import StudioClients from "@/pages/studio/StudioClients";
import StudioLibrary from "@/pages/studio/StudioLibrary";
import StudioProfile from "@/pages/studio/StudioProfile";
import StudioDocuments from "@/pages/studio/StudioDocuments";
import StudioFinances from "@/pages/studio/StudioFinances";
import PeppolLanding from "@/pages/studio/PeppolLanding";
import CountryLanding from "@/pages/CountryLanding";
import DemoPage from "@/pages/DemoPage";
import StudioRegister from "@/pages/studio/StudioRegister";
import PublicQuote from "@/pages/studio/PublicQuote";
import PublicReport from "@/pages/studio/PublicReport";
import StudioSubscription from "@/pages/studio/StudioSubscription";
import StudioTeam from "@/pages/studio/StudioTeam";
import StudioAccountant from "@/pages/studio/StudioAccountant";
import StudioAccountantHub from "@/pages/studio/StudioAccountantHub";
import StudioShopping from "@/pages/studio/StudioShopping";
import StudioIntervention from "@/pages/studio/StudioIntervention";
import StudioInterventions from "@/pages/studio/StudioInterventions";
import StudioRevenue from "@/pages/studio/StudioRevenue";
import StudioInvoices from "@/pages/studio/StudioInvoices";
import StudioPayments from "@/pages/studio/StudioPayments";
import StudioPurchases from "@/pages/studio/StudioPurchases";
import StudioWebRequests from "@/pages/studio/StudioWebRequests";
import StudioMessages from "@/pages/studio/StudioMessages";
import StudioContracts from "@/pages/studio/StudioContracts";
import StudioContract from "@/pages/studio/StudioContract";
import StudioAssistant from "@/pages/studio/StudioAssistant";
import PublicContract from "@/pages/studio/PublicContract";
import PublicFollow from "@/pages/studio/PublicFollow";
import PublicReview from "@/pages/studio/PublicReview";
import PublicRequest from "@/pages/studio/PublicRequest";
import StudioQuotes from "@/pages/studio/StudioQuotes";
import StudioOnboarding from "@/pages/studio/StudioOnboarding";
import StudioInvoice from "@/pages/studio/StudioInvoice";
import StudioPeppol from "@/pages/studio/StudioPeppol";
import StudioPlanning from "@/pages/studio/StudioPlanning";
import PublicInvoice from "@/pages/studio/PublicInvoice";
import PublicPay from "@/pages/studio/PublicPay";
import StudioInvitation from "@/pages/studio/StudioInvitation";
import StudioSites from "@/pages/studio/StudioSites";
import StudioSite from "@/pages/studio/StudioSite";
import StudioEquipment from "@/pages/studio/StudioEquipment";
import PublicPassport from "@/pages/studio/PublicPassport";
import TarifsPage from "@/pages/TarifsPage";
import AboutPage from "@/pages/AboutPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import { ChatWidget } from "@/components/ChatWidget";
import { CookieBanner } from "@/components/CookieBanner";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { LanguageProvider } from "@/i18n";
import { MarketProvider } from "@/market";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/* Le comptable invité ne voit que son espace : on redirige avant que la page cible ne se monte. */
const AccountantGuard = () => {
  const { pathname } = useLocation();
  const accountant = localStorage.getItem("devisto_member_role") === "comptable";
  if (accountant && pathname.startsWith("/studio") && !pathname.startsWith("/studio/comptable") && !pathname.startsWith("/studio/invitation")) {
    return <Navigate to="/studio/comptable" replace />;
  }
  return <Outlet />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

function App() {
  return (
    <LanguageProvider>
    <MarketProvider>
    <div className="App">
      <BrowserRouter>
        <SmoothScroll />
        <ScrollToTop />
        <MaintenanceGate>
        <Routes>
          <Route element={<AccountantGuard />}>
          <Route path="/" element={<Home />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser/:token" element={<ResetPassword />} />
          <Route path="/tarifs" element={<TarifsPage />} />
          <Route path="/mentions-legales" element={<LegalPage />} />
          <Route path="/confidentialite" element={<PrivacyPage />} />
          <Route path="/cgv" element={<TermsPage />} />
          <Route path="/passeport/:code" element={<PublicPassport />} />
          <Route path="/a-propos" element={<AboutPage />} />
          <Route path="/offre-lancement" element={<Navigate to="/" replace />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          {/* Anciennes pages marketplace : retirées du site public */}
          {[
            "/publier-une-demande", "/publier-une-mission", "/inscription-entreprise", "/espace/entreprise",
            "/devenir-partenaire-pro", "/espace/pro", "/espace/client", "/espace/property",
            "/inscription-gestionnaire", "/suivi/:token",
            "/trouver/:slug", "/deblocage/:id", "/mission/:id", "/carte", "/assistant-devis",
          ].map((path) => (
            <Route key={path} path={path} element={<Navigate to="/" replace />} />
          ))}
          <Route path="/inscription" element={<Navigate to="/studio/inscription" replace />} />
          <Route path="/studio" element={<StudioHome />} />
          <Route path="/studio/dossiers" element={<StudioDossiers />} />
          <Route path="/studio/dossiers/:id" element={<StudioDossier />} />
          <Route path="/studio/devis/:id" element={<StudioQuote />} />
          <Route path="/studio/clients" element={<StudioClients />} />
          <Route path="/studio/bibliotheque" element={<StudioLibrary />} />
          <Route path="/studio/profil" element={<StudioProfile />} />
          <Route path="/studio/documents" element={<StudioDocuments />} />
          <Route path="/studio/finances" element={<StudioFinances />} />
          <Route path="/facture-peppol-2026" element={<PeppolLanding />} />
          <Route path="/belgique" element={<CountryLanding slug="belgique" />} />
          <Route path="/france" element={<CountryLanding slug="france" />} />
          <Route path="/espagne" element={<CountryLanding slug="espagne" />} />
          <Route path="/luxembourg" element={<CountryLanding slug="luxembourg" />} />
          <Route path="/video-demo" element={<DemoPage />} />
          <Route path="/studio/inscription" element={<StudioRegister />} />
          <Route path="/studio/abonnement" element={<StudioSubscription />} />
          <Route path="/studio/abonnement/succes" element={<StudioSubscription />} />
          <Route path="/studio/equipe" element={<StudioTeam />} />
          <Route path="/studio/comptable" element={<StudioAccountant />} />
          <Route path="/studio/mon-comptable" element={<StudioAccountantHub />} />
          <Route path="/studio/materiel" element={<StudioShopping />} />
          <Route path="/studio/interventions" element={<StudioInterventions />} />
          <Route path="/studio/chiffre-affaires" element={<StudioRevenue />} />
          <Route path="/studio/bienvenue" element={<StudioOnboarding />} />
          <Route path="/studio/devis" element={<StudioQuotes />} />
          <Route path="/studio/factures" element={<StudioInvoices />} />
          <Route path="/studio/avoirs" element={<StudioInvoices creditNotes />} />
          <Route path="/studio/reglements" element={<StudioPayments />} />
          <Route path="/studio/achats" element={<StudioPurchases />} />
          <Route path="/studio/demandes" element={<StudioWebRequests />} />
          <Route path="/studio/messages" element={<StudioMessages />} />
          <Route path="/studio/contrats" element={<StudioContracts />} />
          <Route path="/studio/assistant" element={<StudioAssistant />} />
          <Route path="/studio/contrats/:id" element={<StudioContract />} />
          <Route path="/contrat/:token" element={<PublicContract />} />
          <Route path="/suivi-client/:token" element={<PublicFollow />} />
          <Route path="/avis/:token" element={<PublicReview />} />
          <Route path="/demande/:slug" element={<PublicRequest />} />
          <Route path="/studio/factures/:id" element={<StudioInvoice />} />
          <Route path="/studio/peppol" element={<StudioPeppol />} />
          <Route path="/studio/planning" element={<StudioPlanning />} />
          <Route path="/studio/batiments" element={<StudioSites />} />
          <Route path="/studio/batiments/:id" element={<StudioSite />} />
          <Route path="/studio/equipements/:id" element={<StudioEquipment />} />
          <Route path="/pt/:code" element={<PublicPassport />} />
          <Route path="/facture/:token" element={<PublicInvoice />} />
          <Route path="/payer/:token" element={<PublicPay />} />
          <Route path="/studio/interventions/:id" element={<StudioIntervention />} />
          <Route path="/studio/invitation/:token" element={<StudioInvitation />} />
          <Route path="/devis/:token" element={<PublicQuote />} />
          <Route path="/rapport/:token" element={<PublicReport />} />
          <Route path="*" element={<NotFoundPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          </Route>
        </Routes>
        <ChatWidget />
        <CookieBanner />
        </MaintenanceGate>
        <Toaster position="bottom-center" richColors offset={90} mobileOffset={92} />
      </BrowserRouter>
    </div>
    </MarketProvider>
    </LanguageProvider>
  );
}

export default App;
